import { useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button, Col, Modal, Overlay, Popover, Row, Table } from "react-bootstrap";
import ReactToPrint from "react-to-print";
import { connect } from "react-redux";
import { Link, useHistory, useLocation } from "react-router-dom";
import axios from "../../../apis/Api";
import { VzPageContainer } from "../../../layouts/VzPageContainer";
import {
  fetchSCRV2SummaryReport,
  finalizeSCRV2SummaryReport,
  isSCRV2SummaryComplete,
  normalizeSCRV2SummaryReport,
  peekSCRV2SummaryReportCache,
  prefetchSCRV2CompleteDetail,
  buildSCRV2DetailPreviewFromSummary,
} from "./SCRV2DetailReportActions";
import SCRV2SummaryReportPrint from "./SCRV2SummaryReportPrint";
import "./SCRV2SummaryReport.css";
import { GET_SCR_V2_SUMMARY_REPORT } from "../../../Types";
import { formatCurrency, getScrV2VarianceLabel } from "./SCRV2InvoiceDetailHelpers";
import {
  formatScrV2DurationDays,
  renderScrV2IdentityDate,
} from "./SCRV2IdentityPresentation";

import {
  renderScrV2SummaryColumn,
  SCR_V2_NESTED_CARD_STYLE,
  ScrV2SummaryKpi,
} from "./SCRV2SummaryUiShared";
import {
  ScrV2CollectionEfficiencyRing as CollectionEfficiencyRing,
  ScrV2ExpandablePaymentGroup as ExpandablePaymentGroup,
  ScrV2PaymentTypeRow as PaymentTypeRow,
  ScrV2SummaryAmountRow as AmountRow,
  ScrV2SummaryPanel as Panel,
  formatScrV2SummaryCurrencyOrPlaceholder as formatCurrencyOrPlaceholder,
  sumScrV2TransactionCounts as sumTransactionCounts,
} from "./SCRV2SummaryReportComponents";
import { matchesScrV2Id, resolveInitialScrV2Report } from "./SCRV2ReportStateHelpers";

const NESTED_CARD_STYLE = SCR_V2_NESTED_CARD_STYLE;
const renderSummaryColumn = renderScrV2SummaryColumn;
const SummaryKpi = ScrV2SummaryKpi;

const resolveInitialSummaryReport = (scrId, location, reduxSummary) =>
  resolveInitialScrV2Report({
    scrId,
    location,
    reduxReport: reduxSummary,
    previewStateKey: "preview",
    peekCache: peekSCRV2SummaryReportCache,
    isComplete: isSCRV2SummaryComplete,
    normalize: normalizeSCRV2SummaryReport,
  });

const SCRV2SummaryReport = (props) => {
  const history = useHistory();
  const location = useLocation();
  const printRef = useRef();
  const printReportRef = useRef(null);
  const loadRequestRef = useRef(0);
  const [vizLinkGroupOpen, setVizLinkGroupOpen] = useState(false);
  const [nonVizLinkGroupOpen, setNonVizLinkGroupOpen] = useState(false);
  const [recoveryGroupOpen, setRecoveryGroupOpen] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [isPrintMountReady, setIsPrintMountReady] = useState(false);
  const [showFinalizeConfirm, setShowFinalizeConfirm] = useState(false);
  const [showBlockerPopover, setShowBlockerPopover] = useState(false);
  const blockerTriggerRef = useRef(null);
  const scrId = props.match?.params?.scrId;
  const [report, setReport] = useState(() =>
    resolveInitialSummaryReport(scrId, location, props.summary)
  );
  const [isRefreshing, setIsRefreshing] = useState(
    () => !isSCRV2SummaryComplete(resolveInitialSummaryReport(scrId, location, props.summary))
  );

  const hasFullReport = isSCRV2SummaryComplete(report);
  const showAmountPlaceholders = isRefreshing && !hasFullReport;

  const highlights = report.highlights || {};
  const paymentDue = report.paymentDue || {};
  const paymentReceived = report.paymentReceived || {};
  const reversals = report.reversals || {};
  const finalization = report.finalization || {};

  const collectionRate = useMemo(
    () => Number(highlights.collectionRatePercent || 0),
    [highlights.collectionRatePercent]
  );
  const variance = useMemo(
    () => Number(highlights.varianceAmount || 0),
    [highlights.varianceAmount]
  );
  const varianceLabel = getScrV2VarianceLabel(variance);
  const varianceIconBg =
    variance > 0 ? "bg-danger" : variance < 0 ? "bg-warning" : "bg-info";

  const nonVizLinkPayments = useMemo(
    () => paymentReceived.nonVizLinkBreakdown || [],
    [paymentReceived.nonVizLinkBreakdown]
  );
  const vizLinkTransactionCount = useMemo(
    () => sumTransactionCounts(paymentReceived.vizLinkBreakdown),
    [paymentReceived.vizLinkBreakdown]
  );
  const nonVizLinkTransactionCount = useMemo(
    () => sumTransactionCounts(nonVizLinkPayments),
    [nonVizLinkPayments]
  );
  const recoveryPayments = useMemo(
    () => paymentReceived.recoveryBreakdown || [],
    [paymentReceived.recoveryBreakdown]
  );
  const recoveryTransactionCount = useMemo(
    () => sumTransactionCounts(recoveryPayments),
    [recoveryPayments]
  );

  useEffect(() => {
    if (!scrId) {
      return;
    }

    const requestId = ++loadRequestRef.current;
    const initialReport = resolveInitialSummaryReport(scrId, location, props.summary);
    const hasCachedReport = isSCRV2SummaryComplete(initialReport);
    setReport(initialReport);
    setIsRefreshing(!hasCachedReport);

    const shouldBackgroundRefresh = hasCachedReport && !initialReport.isFinalized;
    if (shouldBackgroundRefresh) {
      setIsRefreshing(true);
    }

    const fetchPromise = shouldBackgroundRefresh
      ? fetchSCRV2SummaryReport(scrId, { forceRefresh: true })
      : fetchSCRV2SummaryReport(scrId);

    fetchPromise
      .then((payload) => {
        if (requestId !== loadRequestRef.current) {
          return;
        }

        setReport(payload);
        props.syncSummaryReport(payload);
      })
      .catch((error) => {
        if (requestId !== loadRequestRef.current) {
          return;
        }

        if (axios.isCancel?.(error) || error?.code === "ERR_CANCELED") {
          return;
        }

        if (!isSCRV2SummaryComplete(initialReport)) {
          setReport(initialReport.scrNo || initialReport.distributorName ? initialReport : { scrId });
        }
      })
      .finally(() => {
        if (requestId === loadRequestRef.current) {
          setIsRefreshing(false);
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrId]);

  useEffect(() => {
    if (
      !scrId ||
      !props.summary?.scrId ||
      !matchesScrV2Id(props.summary.scrId, scrId) ||
      !isSCRV2SummaryComplete(props.summary)
    ) {
      return;
    }

    setReport(normalizeSCRV2SummaryReport(props.summary, scrId));
    setIsRefreshing(false);
  }, [props.summary, scrId]);

  const blockingReasons = useMemo(
    () =>
      !report.isFinalized && finalization.blockingReasons?.length > 0
        ? finalization.blockingReasons
        : [],
    [report.isFinalized, finalization.blockingReasons]
  );

  const blockingInvoices = useMemo(
    () =>
      !report.isFinalized && Array.isArray(finalization.blockingInvoices)
        ? finalization.blockingInvoices
        : [],
    [report.isFinalized, finalization.blockingInvoices]
  );

  const handleBlockerClick = (event) => {
    if (blockingInvoices.length === 0) {
      return;
    }

    blockerTriggerRef.current = event.currentTarget;
    setShowBlockerPopover((open) => !open);
  };

  useEffect(() => {
    if (blockingInvoices.length === 0 || report.isFinalized) {
      setShowBlockerPopover(false);
    }
  }, [blockingInvoices.length, report.isFinalized]);

  const handleFinalize = () => {
    setShowFinalizeConfirm(true);
  };

  const handleConfirmFinalize = async () => {
    setShowFinalizeConfirm(false);
    setIsFinalizing(true);
    try {
      await props.finalizeSCRV2SummaryReport({ scrId, currentReport: report });
    } catch {
      // Error toast handled in action.
    } finally {
      setIsFinalizing(false);
    }
  };

  const handleBeforePrint = () =>
    new Promise((resolve) => {
      printReportRef.current = report;
      setIsPrintMountReady(true);
      window.setTimeout(resolve, 120);
    });

  const handleAfterPrint = () => {
    setIsPrintMountReady(false);
    printReportRef.current = null;
  };

  return (
    <VzPageContainer title="SCR Summary Report">
      <div className="scr-v2-summary-screen">
        <div className="scr-summary-page-header d-flex align-items-center flex-wrap mb-2">
          <div className="scr-summary-page-header__title">
            <h5 className="mb-0 d-flex align-items-center flex-wrap">
              <b>SCR Summary Report</b>
              {isRefreshing ? (
                <span className="badge badge-light border text-muted ml-2 mt-1 mt-sm-0 d-print-none font-weight-normal">
                  <i className="fas fa-sync-alt fa-spin mr-1" />
                  Updating
                </span>
              ) : null}
            </h5>
          </div>

          {blockingReasons.length > 0 ? (
            <div className="scr-summary-page-header__center flex-grow-1 d-print-none px-2">
              <div className="scr-summary-blocker scr-summary-blocker--inline">
                <i className="fas fa-exclamation-triangle mr-1" />
                <span className="font-weight-bold">Finalization blockers:</span>{" "}
                {blockingInvoices.length > 0 ? (
                  <button
                    type="button"
                    className="scr-summary-blocker__trigger btn btn-link p-0 align-baseline"
                    onClick={handleBlockerClick}
                    aria-expanded={showBlockerPopover}
                  >
                    {blockingReasons.join(" · ")}
                    <i className="fas fa-info-circle ml-1" aria-hidden="true" />
                  </button>
                ) : (
                  blockingReasons.join(" · ")
                )}
                {blockingInvoices.length > 0 ? (
                  <Overlay
                    show={showBlockerPopover}
                    target={blockerTriggerRef.current}
                    placement="bottom"
                    rootClose
                    onHide={() => setShowBlockerPopover(false)}
                  >
                    <Popover
                      id="scr-summary-blocker-invoices"
                      className="scr-summary-blocker-popover"
                    >
                      <Popover.Header as="h3">Invoice Outstanding</Popover.Header>
                      <Popover.Body>
                        <ul className="scr-summary-blocker-popover__list mb-0 pl-3">
                          {blockingInvoices.map((invoice, index) => (
                            <li key={`${invoice.invoiceNumber || "inv"}-${index}`}>
                              <div>
                                Invoice:{" "}
                                <strong>{invoice.invoiceNumber || "-"}</strong>
                              </div>
                              <div>
                                Outstanding:{" "}
                                <strong>
                                  {formatCurrency(invoice.outstandingAmount)}
                                </strong>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </Popover.Body>
                    </Popover>
                  </Overlay>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="scr-summary-page-header__spacer flex-grow-1" aria-hidden="true" />
          )}

          <div className="scr-summary-page-header__status text-right">
            <Badge bg={report.isFinalized ? "success" : "warning"}>
              {report.isFinalized ? "Finalized" : "Open"}
            </Badge>
            <div className="text-muted small">
              SCR No: <span className="text-primary font-weight-bold">{report.scrNo || "-"}</span>
            </div>
          </div>
        </div>

        <div className="card border mb-2 scr-summary-info-card" style={NESTED_CARD_STYLE}>
          <div className="card-body py-2 px-2">
            <Row className="align-items-center mx-0">
              {renderSummaryColumn("Distributor", report.distributorName, { lg: 3 })}
              {renderSummaryColumn("Distribution Center", report.distCenterName, { lg: 3 })}
              {renderSummaryColumn(
                "DLM Details",
                <>
                  {report.dlmName || report.dlmUserName || "-"}
                  {report.deliveryQRCode ? (
                    <small className="scr-summary-info-card__subvalue d-block">
                      {report.deliveryQRCode}
                    </small>
                  ) : null}
                </>,
                { lg: 2 }
              )}
              {renderSummaryColumn(
                "Business Date",
                <>
                  {renderScrV2IdentityDate(report.businessDate)}
                  {report.finalizedDate ? (
                    <small className="scr-summary-info-card__subvalue d-block">
                      Finalized: {renderScrV2IdentityDate(report.finalizedDate)}
                    </small>
                  ) : null}
                </>,
                { lg: 2 }
              )}
              {renderSummaryColumn("Duration", formatScrV2DurationDays(report.durationDays), {
                lg: 2,
              })}
            </Row>
          </div>
        </div>

        <Row className="mb-2 mx-0 align-items-stretch scr-summary-kpi-row">
          <SummaryKpi
            colClassName="ps-0 pe-lg-1"
            icon="fa-file-invoice"
            iconBg="bg-primary"
            title="Total Payment Due"
            amount={formatCurrencyOrPlaceholder(paymentDue.grandTotalDue, showAmountPlaceholders)}
            detail={
              showAmountPlaceholders
                ? "Loading invoice totals..."
                : `${highlights.totalInvoices || 0} invoice(s) total`
            }
          />
          <SummaryKpi
            colClassName="px-lg-1"
            icon="fa-money-bill-wave"
            iconBg="bg-success"
            title="Total Payment Received"
            amount={formatCurrencyOrPlaceholder(highlights.totalReceived, showAmountPlaceholders)}
            detail={
              showAmountPlaceholders
                ? "Calculating collection rate..."
                : `${collectionRate}% collection rate`
            }
          />
          <SummaryKpi
            colClassName="ps-lg-1 pe-0"
            icon="fa-balance-scale"
            iconBg={varianceIconBg}
            title={`${varianceLabel} (Variance)`}
            amount={formatCurrencyOrPlaceholder(Math.abs(variance), showAmountPlaceholders)}
            detail={variance === 0 ? "Fully balanced" : `${varianceLabel} amount`}
          />
        </Row>

        <Row className="mb-2 mx-0 align-items-stretch scr-summary-middle-row">
          <Col lg={6} className="mb-2 mb-lg-0 d-flex flex-column ps-0 pe-lg-1">
            <Panel
              title="Payment Due Breakdown"
              icon="fa-file-invoice"
              iconBg="bg-primary"
            >
              <AmountRow
                label="Total Original Invoice Amount"
                value={paymentDue.totalOriginalInvoicesAmount}
                showPlaceholder={showAmountPlaceholders}
              />
              <AmountRow
                label="Adjustments"
                value={paymentDue.totalAdjustmentAmount}
                signed
                showPlaceholder={showAmountPlaceholders}
              />
              <AmountRow
                label="Total Invoice Amount"
                value={paymentDue.totalInvoicesAmount}
                showPlaceholder={showAmountPlaceholders}
              />
              <AmountRow
                label="Credit Amount"
                value={paymentDue.totalCreditAmount}
                deduct={paymentDue.totalCreditAmount > 0}
                showPlaceholder={showAmountPlaceholders}
              />
              <AmountRow
                label="Credit Recovery"
                value={paymentDue.totalRecoveryAmount}
                signed
                showPlaceholder={showAmountPlaceholders}
              />
              <AmountRow
                label="GRAND TOTAL DUE"
                value={paymentDue.grandTotalDue}
                strong
                highlight
                showPlaceholder={showAmountPlaceholders}
              />
            </Panel>

            <div className="mt-0">
              <Panel
                title="Reversals"
                icon="fa-undo"
                iconBg="bg-warning"
                bodyClassName="py-2 pb-1"
                badge={
                  showAmountPlaceholders
                    ? "…"
                    : `${reversals.reversalCount || 0} reversal(s)`
                }
              >
                <AmountRow
                  label="Total Transaction Reversals"
                  value={reversals.totalReversalAmount}
                  showPlaceholder={showAmountPlaceholders}
                />
              </Panel>
            </div>
          </Col>

          <Col lg={6} className="d-flex ps-lg-1 pe-0">
            <div
              className="card border w-100 d-flex flex-column align-items-center justify-content-center scr-summary-efficiency-card"
              style={{ ...NESTED_CARD_STYLE, borderStyle: "dashed" }}
            >
              <div className="w-100">
                <CollectionEfficiencyRing
                  rate={collectionRate}
                  showPlaceholder={showAmountPlaceholders}
                />
              </div>
              <div className="mt-2 mb-0 text-center">
                <Button
                  as={Link}
                  to={{
                    pathname: `/SCRV2CompleteDetailReport/${scrId}`,
                    state: {
                      detailPreview: buildSCRV2DetailPreviewFromSummary(report),
                    },
                  }}
                  variant="primary"
                  className="font-weight-bold px-4 rounded-pill"
                  style={{ backgroundColor: "#ea5f2d", borderColor: "#ea5f2d", color: "#fff" }}
                  onMouseEnter={() => prefetchSCRV2CompleteDetail(scrId)}
                  onFocus={() => prefetchSCRV2CompleteDetail(scrId)}
                >
                  View Invoice Wise Details
                  <i className="fa fa-arrow-right ml-2" />
                </Button>
              </div>
            </div>
          </Col>
        </Row>

        <Row className="mx-0 mb-2">
          <Col lg={12} className="mb-2 mb-lg-0 px-0">
            <Panel
              title="Payment Received Breakdown"
              icon="fa-credit-card"
              iconBg="bg-info"
              bodyClassName="p-0"
            >
              <Table responsive size="sm" className="mb-0 scr-summary-payment-table">
                <thead className="thead-light">
                  <tr>
                    <th className="pl-3">Payment Type</th>
                    <th className="text-center">Count</th>
                    <th className="text-right pr-3">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {showAmountPlaceholders ? (
                    <tr>
                      <td colSpan="3" className="text-center text-muted py-2">
                        Loading payment breakdown...
                      </td>
                    </tr>
                  ) : null}

                  {!showAmountPlaceholders &&
                    (paymentReceived.vizLinkBreakdown || []).length === 0 &&
                    nonVizLinkPayments.length === 0 &&
                    recoveryPayments.length === 0 &&
                    !paymentReceived.nonVizLinkAmountInHand &&
                    !paymentReceived.nonVizLinkAmountDepositedInBank &&
                    !paymentDue.totalRecoveryAmount ? (
                    <tr>
                      <td colSpan="3" className="text-center text-muted py-2">
                        No payment breakdown available.
                      </td>
                    </tr>
                  ) : null}

                  {!showAmountPlaceholders &&
                    (paymentReceived.vizLinkBreakdown || []).length > 0 ? (
                    <ExpandablePaymentGroup
                      title="VizLink Collection"
                      amount={paymentReceived.totalVizLinkCollectionAmount}
                      count={vizLinkTransactionCount}
                      open={vizLinkGroupOpen}
                      onToggle={() => setVizLinkGroupOpen((open) => !open)}
                    >
                      {(paymentReceived.vizLinkBreakdown || []).map((item) => (
                        <PaymentTypeRow
                          key={`viz-${item.paymentType}`}
                          label={item.paymentType}
                          count={item.transactionCount}
                          amount={item.amount}
                          indent
                        />
                      ))}
                    </ExpandablePaymentGroup>
                  ) : null}

                  {!showAmountPlaceholders &&
                    (nonVizLinkPayments.length > 0 ||
                      paymentReceived.totalNonVizLinkCollectionAmount > 0) ? (
                    <ExpandablePaymentGroup
                      title="Non-VizLink Collection"
                      amount={paymentReceived.totalNonVizLinkCollectionAmount}
                      count={nonVizLinkTransactionCount}
                      open={nonVizLinkGroupOpen}
                      onToggle={() => setNonVizLinkGroupOpen((open) => !open)}
                    >
                      {nonVizLinkPayments.map((item) => (
                        <PaymentTypeRow
                          key={`nv-${item.paymentType}`}
                          label={item.paymentType}
                          count={item.transactionCount}
                          amount={item.amount}
                          indent
                        />
                      ))}
                    </ExpandablePaymentGroup>
                  ) : null}

                  {!showAmountPlaceholders &&
                    (recoveryPayments.length > 0 || paymentDue.totalRecoveryAmount > 0) ? (
                    <ExpandablePaymentGroup
                      title="Recovery Collection"
                      amount={paymentDue.totalRecoveryAmount}
                      count={recoveryTransactionCount}
                      open={recoveryGroupOpen}
                      onToggle={() => setRecoveryGroupOpen((open) => !open)}
                    >
                      {recoveryPayments.map((item) => (
                        <PaymentTypeRow
                          key={`recovery-${item.paymentType}`}
                          label={item.paymentType}
                          count={item.transactionCount}
                          amount={item.amount}
                          indent
                        />
                      ))}
                    </ExpandablePaymentGroup>
                  ) : null}

                  {!showAmountPlaceholders ? (
                    <>
                      <PaymentTypeRow
                        label="Amount Collected"
                        count={paymentReceived.amountCollectedTransactionCount}
                        amount={paymentReceived.amountCollected}
                      />
                      <PaymentTypeRow
                        label="Amount Deposited in Bank"
                        count={paymentReceived.nonVizLinkDepositedTransactionCount}
                        amount={paymentReceived.nonVizLinkAmountDepositedInBank}
                      />
                      <PaymentTypeRow
                        label="Amount in Hand"
                        count={paymentReceived.nonVizLinkInHandTransactionCount}
                        amount={paymentReceived.nonVizLinkAmountInHand}
                      />
                    </>
                  ) : null}
                </tbody>
              </Table>
            </Panel>
          </Col>
        </Row>
      </div>

      {isPrintMountReady && printReportRef.current ? (
        <div style={{ display: "none" }} aria-hidden="true">
          <SCRV2SummaryReportPrint ref={printRef} report={printReportRef.current} />
        </div>
      ) : null}

      <section className="d-print-none mt-2 pt-2 border-top scr-summary-actions">
        <Row className="mx-0">
          <Col className="px-0 d-flex justify-content-between align-items-center flex-wrap">
            <ReactToPrint
              trigger={() => (
                <button style={{ minInlineSize: 100 }} className="btn btn-default" type="button">
                  <i className="fa fa-print mr-1" />
                  Print
                </button>
              )}
              content={() => printRef.current}
              onBeforeGetContent={handleBeforePrint}
              onAfterPrint={handleAfterPrint}
              pageStyle={`
                @page {
                  size: A4 portrait;
                  margin: 10mm;
                }
                @media print {
                  html, body {
                    width: 100% !important;
                    height: auto !important;
                    margin: 0 !important;
                    padding: 0 !important;
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                  }
                  .scr-v2-summary-print-view {
                    width: 100% !important;
                    max-width: 100% !important;
                    margin: 0 !important;
                    padding: 0 !important;
                  }
                }
              `}
            />
            <div className="mt-2 mt-sm-0">
              <button
                style={{ minInlineSize: 100 }}
                className="btn btn-default mr-2"
                type="button"
                onClick={() => history.push("/SCRV2DetailReport")}
              >
                Close
              </button>
              {!report.isFinalized && (
                <button
                  style={{ minInlineSize: 100 }}
                  className="btn btn-success"
                  type="button"
                  disabled={!finalization.canFinalize || isRefreshing || isFinalizing}
                  onClick={handleFinalize}
                >
                  {isFinalizing ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-1" />
                      Finalizing...
                    </>
                  ) : (
                    "Finalize Report"
                  )}
                </button>
              )}
            </div>
          </Col>
        </Row>
      </section>

      <Modal
        show={showFinalizeConfirm}
        onHide={() => setShowFinalizeConfirm(false)}
        centered
        backdrop="static"
        className="scr-finalize-modal"
      >
        <Modal.Body>
          <div className="scr-finalize-modal__icon">
            <i className="fas fa-exclamation-triangle" aria-hidden="true" />
          </div>
          <div className="scr-finalize-modal__title">Finalize SCR Report?</div>
          <p className="scr-finalize-modal__message mb-0">
            This will lock the SCR summary for today. You will not be able to edit
            collections or reversals after finalization.
          </p>
          {report.scrNo ? (
            <span className="scr-finalize-modal__scr-no">SCR No: {report.scrNo}</span>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowFinalizeConfirm(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            className="scr-finalize-modal__confirm-btn"
            onClick={handleConfirmFinalize}
          >
            Yes, Finalize
          </Button>
        </Modal.Footer>
      </Modal>
    </VzPageContainer>
  );
};

const mapStateToProps = (state) => ({
  summary: state.SCRV2DetailReport.summary,
});

const mapDispatchToProps = (dispatch) => ({
  finalizeSCRV2SummaryReport: (request) => dispatch(finalizeSCRV2SummaryReport(request)),
  syncSummaryReport: (payload) =>
    dispatch({
      type: GET_SCR_V2_SUMMARY_REPORT,
      payload,
    }),
});

export default connect(mapStateToProps, mapDispatchToProps)(SCRV2SummaryReport);
